import { Origin, useAppAPIKeyCreate } from "@/api";
import { TypographyMuted } from "@/components/typography/Muted";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { toast } from "@/components/ui/use-toast";
import { copyToClipboard } from "@/lib/js-helpers/copyToClipboard";
import { getFormData } from "@/lib/js-helpers/getFormData";
import { Dispatch, FormEvent, SetStateAction, useRef, useState } from "react";
import { PartialApp } from "../../types/app.type";

interface Props {
  app: PartialApp;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function AddAPIKeyModal({ app, setOpen }: Props) {
  const [loading, setLoading] = useState(false);
  const [disableAdd, setDisableAdd] = useState(true);
  const [inputLength, setInputLength] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  let id;
  let origin;
  if (app.origin === "organization") {
    id = app.defaultDeployment?.id;
    origin = Origin.Organization;
  }
  if (app.origin === "subscription") {
    id = app.subscription?.id;
    origin = Origin.Subscription;
  }
  const [createAPIKey] = useAppAPIKeyCreate({
    id,
    appSlug: app.slug,
    subscriptionId: app.subscription?.id,
    origin
  });

  function handleAdd(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const { name } = getFormData(e.currentTarget, ["name"] as const);
    if (!name) return;

    setLoading(true);
    createAPIKey({ variables: { name } })
      .then((res) => {
        if (!res.data) return;
        const newKey =
          "appDeploymentAPIKeyCreate" in res.data
            ? res.data.appDeploymentAPIKeyCreate
            : res.data.appSubscriptionAPIKeyCreate;

        if (newKey.__typename === "AppAPIKeyAlreadyExists") {
          toast({
            title: "API key already exists",
            description:
              "An API key with this name already exists. Please choose another name and try again.",
            variant: "destructive"
          });
          return;
        }

        if (newKey.__typename === "AppAPIKeyInvalidName") {
          toast({
            title: "Invalid API key name",
            description:
              "The input name is invalid. Please select a valid name and try again."
          });
          return;
        }

        if (newKey.__typename === "AppAPIKey")
          copyToClipboard(newKey.secret).then(() => {
            toast({
              title: "New API key added",
              description: "The API key has been copied to the clipboard."
            });
          });
        setOpen(false);
      })
      .catch((error) => {
        toast({
          title: "Unable to add API key",
          description:
            "An error occurred while adding the API key. Please try again later."
        });
        console.error(error);
      })
      .finally(() => setLoading(false));
  }

  return (
    <Dialog.Root open onOpenChange={setOpen}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Add API Key</Dialog.Title>
          <Dialog.Description>
            This will enable access to the API. Access to the API can be revoked
            at any time by deleting the key.
          </Dialog.Description>
        </Dialog.Header>
        <form
          onSubmit={handleAdd}
          onChange={(e) => setDisableAdd(!e.currentTarget.checkValidity())}
          className="mt-2"
        >
          <div className="flex justify-between">
            <Label>Name</Label>
            <TypographyMuted>{inputLength} / 40</TypographyMuted>
          </div>
          <Input
            ref={inputRef}
            autoFocus
            required
            maxLength={40}
            name="name"
            onChange={(e) => setInputLength(e.currentTarget.value.length)}
            className="my-1"
          />
          <TypographyMuted className="italic">
            API Key names must be unique.
          </TypographyMuted>

          <Dialog.Footer className="mt-8 flex items-center">
            {loading && <LoadingCircle />}
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button type="submit" disabled={disableAdd}>
              Add
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default AddAPIKeyModal;
